import {NoteGroupIcon} from "crqlar-react-common/dist/components/note/NoteGroupIcon";
import {useNoteGroupIndex} from "crqlar-react-common/dist/utils/hooks/notes/useNoteGroupIndex";
import {
    Alert,
    Autocomplete,
    Box,
    ButtonGroup,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField
} from "@mui/material";
import Button from "@mui/material/Button";
import {LoadingButton} from "@mui/lab";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {useServicesForSector} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useCurrentApp, useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {ReservationGroup} from "crqlar-react-common/dist/models/reservation/reservationGroup.model";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import Typography from "@mui/material/Typography";
import ProfileInfoRow from "crqlar-react-common/dist/components/ProfileInfoRow";
import CDatePicker from "crqlar-react-common/dist/components/CDatePicker";
import {
    useAllRootResourcesForSector,
    useResourcesWithAvailability
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {
    useAllResourceCategoriesForSector
} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {isResourceAvailable} from "crqlar-react-common/dist/utils/resource.util";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {addHours, addMinutes} from "crqlar-react-common/dist/utils/date.util";
import DeleteIcon from "@mui/icons-material/Delete";
import SimpleDialog from "crqlar-react-common/dist/components/SimpleDialog";
import {Comment} from "@mui/icons-material";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {ReservationActions, useReservationsState} from "../../../store/reservations.reducer";
import {useDispatch} from "react-redux";
import {useMutation, useQueryClient} from "react-query";
import {ReservationGroupService} from "crqlar-react-common/dist/services/reservation/reservationGroup.service";
import {useSnackbar} from 'notistack'
import {useSectorReservations} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {ServiceType} from "crqlar-react-common/dist/models/service/serviceListRequest.dto";
import ReservationProfiles from "../reservationDialog/ReservationProfiles";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {RESERVATIONS_QUERY_KEY} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";

interface Props {
    open: boolean;
    onClose: () => void;
}

const AddNewPackageReservationsDialog = (props: Props) => {

    const {t} = useTranslation();
    // Read-only users may view a package, but not create reservations from it.
    const readOnly = useReadOnly();

    const appId = useCurrentAppId()
    const appData = useCurrentApp()
    const sector = useSector(appData?.configuration?.sectorId)
    const packageServices = useServicesForSector(sector?.id, ServiceType.PACKAGE)
    const regularServices = useServicesForSector(sector?.id, ServiceType.REGULAR)
    const [tmpServices, setTmpServices] = useState<Service[]>([])
    const dispatch = useDispatch();
    const {enqueueSnackbar} = useSnackbar()

    const [chosenProfile, setChosenProfile] = useState<Profile>()

    // Split "Create reservation ▾" CTA: main action creates silently, the menu item also sends the e-mail.
    const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null)
    const createButtonGroupRef = useRef<HTMLDivElement>(null)

    const {
        tmpReservationGroup,
        tmpReservationGroupReservations,
    } = useReservationsState(appId)

    const queryClient = useQueryClient()

    // Maps the in-progress group + reservations into the create payload. Shared by the plain
    // "create" and the "create & send no-show link" flows so both build the package identically.
    const buildPackagePayload = () => ({
        reservationGroup: tmpReservationGroup,
        reservations: tmpReservationGroupReservations.map(r => ({
            ...r,
            profileIds: tmpReservationGroup?.profileIds,
            status: r.expectedStartDate && r.expectedEndDate ? ReservationStatus.CONFIRMED : ReservationStatus.PENDING
        }))
    })

    // Closes the dialog and clears the in-progress group after a successful create.
    const resetAndCloseDialog = () => {
        queryClient.invalidateQueries(RESERVATIONS_QUERY_KEY)
        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId: appId,
            payload: {
                tmpReservationGroupDialogOpen: false,
                tmpReservationGroup: undefined,
                tmpReservationGroupReservations: []
            }
        })
    }

    const createReservationGroupMut = useMutation(
        'createReservationGroup',
        // allowNotify carries the staff's explicit intent: the split-button main action passes
        // false (silent create), the "& send e-mail" menu item passes true. The backend then sends
        // a SINGLE package confirmation for the primary reservation instead of one email per member.
        async (allowNotify: boolean = false) => {
            try {
                await ReservationGroupService.createReservationGroupWithReservations(buildPackagePayload(), allowNotify)
                enqueueSnackbar(t("Package created"), {variant: 'success'});
                resetAndCloseDialog()
            } catch (error) {
                enqueueSnackbar(t("Package creation failed"), {variant: 'error'});
            }
        }
    )

    const setTmpReservationGroup = (reservationGroup: ReservationGroup) => {
        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId: appId,
            payload: {
                tmpReservationGroup: reservationGroup
            }
        })
    }

    const setTmpReservations = (reservations: Reservation[]) => {
        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId: appId,
            payload: {
                tmpReservationGroupReservations: reservations
            }
        })
    }

    const activateWizard = (reservation: Reservation) => {
        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId: appId,
            payload: {
                tmpReservationGroupDialogOpen: false,
                tmpReservationGroupWizardActive: true,
                tmpReservationGroupWizardReservation: reservation,
                tmpReservationGroupWizardReservationService: [...tmpServices, ...regularServices].find(s => s.id === reservation?.serviceId) || null
            }
        })
    }


    const handlePackageSelect = async (pacakge?: Service) => {
        if (!pacakge) return

        setTmpReservationGroup({
            serviceId: pacakge.id,
            sectorId: sector?.id,
            status: ReservationStatus.CONFIRMED
        } as ReservationGroup)

        const packageServiceIds = pacakge.packageServiceIds || []

        // Without this guard an empty package sent `id: undefined`, which the list endpoint treats as
        // "no filter" — every service of the organization became a package row (each with its own
        // resource picker + availability query) and the dashboard froze.
        if (!packageServiceIds.length) {
            setTmpServices([])
            setTmpReservations([])
            return
        }

        const services = (await ServiceService.getAllServices({
            id: packageServiceIds,
            limit: {
                offset: 0,
                limit: 100
            }
        })).content || []
        setTmpServices(services)

        // Follow the package definition (order + repeated services, e.g. 2x massage) rather than the
        // order the list endpoint happens to return the distinct services in.
        const reservations: Reservation[] = packageServiceIds
            .filter(serviceId => services.some(s => s.id === serviceId))
            .map(serviceId => ({
                serviceId,
                sectorId: sector?.id,
                status: ReservationStatus.CONFIRMED
            }))

        setTmpReservations(reservations)
    }

    const handleCreateProfile = (profile: Profile) => {
        // When creating new profile from reservation dialog, we need to add it to the reservation
        const newProfileIds = [...(tmpReservationGroup?.profileIds || []), profile.id as string]
        setTmpReservationGroup({
            ...tmpReservationGroup,
            profileIds: newProfileIds,
            primaryProfileId: newProfileIds?.[0]
        })
    }

    const selectedPackage = packageServices.find(s => s.id === tmpReservationGroup?.serviceId)

    const renderPackagePicker = () => {
        return (
            <Autocomplete
                id={'service'}
                options={packageServices}
                size={'small'}
                getOptionLabel={(option: Service) => option.name || ''}
                value={selectedPackage || null}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size={'small'}
                        label={t('Package')}
                    />
                )}
                sx={{
                    mt: 2
                }}
                fullWidth
                onChange={(e, value) => {
                    handlePackageSelect(value || undefined)
                }}
            />
        )
    }

    // Tells staff up-front what the package contains and what is charged on top of it.
    const renderPackageSummary = () => {
        if (!selectedPackage) return null

        const includedCount = tmpReservationGroupReservations.filter(r => !r.reservationFromExtraPackageService).length
        const extraCount = tmpReservationGroupReservations.length - includedCount

        if (!selectedPackage.packageServiceIds?.length) {
            return (
                <Alert severity={'warning'}>
                    {t('This package has no services configured. Add services to the package in the service settings first.')}
                </Alert>
            )
        }

        return (
            <Alert severity={'info'} icon={<DynamicFeedIcon fontSize={'inherit'}/>}>
                {t('{{count}} services are included in package {{package}} — each is charged at its own price on one bill', {
                    count: includedCount,
                    package: selectedPackage.name
                })}
                {extraCount > 0 && ` · ${t('{{count}} extra services are added to the bill', {count: extraCount})}`}
            </Alert>
        )
    }

    const renderReservations = () => {
        return (
            <Stack gap={1}>

                <Typography variant={'h5'}>
                    {t('Reservations')}
                </Typography>

                <Typography
                    variant={'subtitle1'}
                    color={'textSecondary'}
                >
                    {t('Schedule reservations immediately, or leave it uncompleted and to it later')}
                </Typography>

                <Stack width={'100%'} gap={2} mt={2}>
                {
                    tmpReservationGroupReservations.map((r, index) => {
                        const service = [...tmpServices, ...regularServices].find(s => s.id === r.serviceId)
                        return (
                            <React.Fragment key={`${r.serviceId}-${index}`}>
                            <SimpleReservationPreview
                                service={service}
                                index={index}
                                reservation={r}
                                setReservation={r => {
                                    const updatedRes = [...tmpReservationGroupReservations]
                                    updatedRes[index] = r
                                    setTmpReservations(updatedRes)
                                }}
                                onDelete={() => {
                                    const updatedRes = [...tmpReservationGroupReservations]
                                    updatedRes.splice(index, 1)
                                    setTmpReservations(updatedRes)
                                }}
                                activateWizard={() => activateWizard(r)}
                            />
                                <Divider />
                            </React.Fragment>
                        )
                    })
                }
                </Stack>


            </Stack>
        )
    }

    /*
        This part is replaced with the new left hand side Profile Matcher component
    */
    // const renderProfiles = () => {
    //     return (
    //         <Stack gap={1}>
    //
    //             <Typography variant={'h5'}>
    //                 {t('Guests')}
    //             </Typography>
    //
    //             <Typography
    //                 variant={'subtitle1'}
    //                 color={'textSecondary'}
    //             >
    //                 {t('These guests will be added to all reservations in the package. If you want to setup custom guests, ignore this and do it manually for each reservation.')}
    //             </Typography>
    //
    //
    //             {
    //                 [...(tmpReservationGroup?.profileIds || []), ''].map((profileId, index) => {
    //                     return (
    //                         <ProfileInfoRow
    //                             id={'profile'}
    //                             profileId={profileId}
    //                             setProfileId={id => {
    //                                 if (!id) return
    //
    //                                 const newProfileIds = [...(tmpReservationGroup?.profileIds || [])]
    //                                 if (index >= newProfileIds.length) {
    //                                     newProfileIds.push(id)
    //                                 } else {
    //                                     newProfileIds[index] = id
    //                                 }
    //                                 setTmpReservationGroup({
    //                                     ...tmpReservationGroup,
    //                                     profileIds: newProfileIds,
    //                                     primaryProfileId: newProfileIds?.[0]
    //                                 })
    //                             }}
    //                             onDelete={() => {
    //                                 const newProfileIds = [...(tmpReservationGroup?.profileIds || [])]
    //                                 newProfileIds.splice(index, 1)
    //                                 setTmpReservationGroup({
    //                                     ...tmpReservationGroup,
    //                                     profileIds: newProfileIds,
    //                                     primaryProfileId: newProfileIds?.[0]
    //                                 })
    //                             }}
    //                         />
    //                     )
    //                 })
    //             }
    //         </Stack>
    //     )
    // }

    const renderAddExtraService = () => {
        return (
            <Autocomplete
                id={'service'}
                options={regularServices}
                size={'small'}
                getOptionLabel={(option: Service) => option.name || ''}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size={'small'}
                        label={t('Add extra service')}
                    />
                )}

                fullWidth
                onChange={(e, value) => {
                    const newRes: Reservation = {
                        serviceId: value?.id,
                        sectorId: sector?.id,
                        status: ReservationStatus.CONFIRMED,
                        // When creating extra service reservation, mark it so that we know it's not part of the original package
                        reservationFromExtraPackageService: true
                    }
                    const newTmpRes = [...tmpReservationGroupReservations, newRes]
                    setTmpReservations(newTmpRes)
                }}
            />
        )
    }

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
            maxWidth={tmpReservationGroup?.serviceId ? "lg" : "xs"}
        >
            <Stack
                direction={'row'}
                width={'100%'}
            >

                {
                    tmpReservationGroup?.serviceId &&
                    <ReservationProfiles
                        // Trick to rerender the whole child component so that it includes the newly added profile
                        key={JSON.stringify(tmpReservationGroup?.profileIds)}
                        reservation={tmpReservationGroup as ReservationGroup}
                        setReservation={res => {
                            const newProfileIds = res.profileIds || []
                            setTmpReservationGroup({
                                ...tmpReservationGroup,
                                profileIds: newProfileIds,
                                primaryProfileId: newProfileIds?.[0]
                            })
                        }}
                        addProfile={profile => {
                            const newProfileIds = [...(tmpReservationGroup?.profileIds || []), profile.id as string]
                            setTmpReservationGroup({
                                ...tmpReservationGroup,
                                profileIds: newProfileIds,
                                primaryProfileId: newProfileIds?.[0]
                            })
                        }}
                        fullProfilePreview={chosenProfile}
                        setFullProfilePreview={setChosenProfile}
                        open={!!tmpReservationGroup}
                        onCreateProfile={handleCreateProfile}
                        onClose={props.onClose}
                    />
                }

                {
                    !chosenProfile &&
                    <Stack
                        direction={'column'}
                        width={tmpReservationGroup?.serviceId ? '75%' : '100%'}
                    >

                        <DialogTitle id="alert-dialog-title">
                            {t('Add new package reservations')}
                        </DialogTitle>

                        <DialogContent>
                            <Stack
                                gap={4}
                            >
                                {renderPackagePicker()}
                                {renderPackageSummary()}
                                {tmpReservationGroup && renderReservations()}
                                {tmpReservationGroup && renderAddExtraService()}
                                {/*{tmpReservationGroup && renderProfiles()}*/}
                            </Stack>
                        </DialogContent>

                        <DialogActions
                            sx={{
                                // Close on the left, CTA on the right; horizontal padding matches
                                // DialogContent (24px) so the CTA's right edge lines up with the
                                // package selector / "add extra service" fields above.
                                justifyContent: 'space-between',
                                px: 3,
                                pb: 2
                            }}
                        >
                            <Button
                                id={'btn-close'}
                                onClick={() => {
                                    dispatch({
                                        type: ReservationActions.SET_RESERVATIONS,
                                        appId: appId,
                                        payload: {
                                            tmpReservationGroupDialogOpen: false,
                                            tmpReservationGroup: undefined,
                                            tmpReservationGroupReservations: []
                                        }
                                    })
                                }}
                            >
                                {t('Close')}
                            </Button>

                            {/* Split "Create reservation ▾" — main action creates silently,
                                the menu item creates and sends the package confirmation e-mail. */}
                            <ButtonGroup ref={createButtonGroupRef} variant={'contained'} color={'secondary'}>
                                <LoadingButton
                                    variant={'contained'}
                                    color={'secondary'}
                                    onClick={() => createReservationGroupMut.mutate(false)}
                                    loading={createReservationGroupMut.isLoading}
                                    disabled={readOnly}
                                >
                                    {t('Create reservation')}
                                </LoadingButton>
                                <Button
                                    variant={'contained'}
                                    color={'secondary'}
                                    size={'small'}
                                    sx={{px: 0.5, minWidth: 'auto'}}
                                    disabled={createReservationGroupMut.isLoading || readOnly}
                                    onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
                                >
                                    <ArrowDropDownIcon/>
                                </Button>
                            </ButtonGroup>
                            <Menu
                                anchorEl={createButtonGroupRef.current}
                                open={Boolean(createMenuAnchor)}
                                onClose={() => setCreateMenuAnchor(null)}
                            >
                                <MenuItem onClick={() => {
                                    createReservationGroupMut.mutate(true)
                                    setCreateMenuAnchor(null)
                                }}>
                                    {t('Create reservation & send e-mail')}
                                </MenuItem>
                            </Menu>

                        </DialogActions>

                    </Stack>
                }

            </Stack>

        </Dialog>
    )
}

interface SimpleReservationPreviewProps {
    index: number;
    service?: Service
    reservation: Reservation;
    setReservation: (reservation: Reservation) => void;
    onDelete: () => void;
    activateWizard: () => void;
}

const SimpleReservationPreview = ({
                                      index,
                                      service,
                                      reservation,
                                      setReservation,
                                      onDelete,
                                      activateWizard
                                  }: SimpleReservationPreviewProps) => {

    const {t} = useTranslation()
    const noteGroupIndex = useNoteGroupIndex()
    // The wizard is an editing affordance — hide it from read-only users.
    const readOnly = useReadOnly()
    const appData = useCurrentApp()
    const sector = useSector(appData?.configuration?.sectorId)
    const services = useServicesForSector(sector?.id)
    const resources = useAllRootResourcesForSector(sector?.id)
    const resourceCategories = useAllResourceCategoriesForSector(sector?.id)
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

    const resourcesWithAvailability =
        useResourcesWithAvailability({
            expectedStartDate: reservation?.expectedStartDate,
            expectedEndDate: reservation?.expectedEndDate,
            sectorId: sector?.id
        })

    const renderDateAndTime = () => {
        return (
            <Stack
                direction="row"
                gap={2}
            >
                <CDatePicker
                    value={reservation?.expectedStartDate}
                    marked={[
                        {
                            date: reservation?.expectedStartDate
                        }
                    ]}
                    onChange={(date) => {
                        let newEndDate = reservation?.expectedEndDate

                        // If the reservation has the service and service duration, set the end date as:
                        // Start date + service duration
                        if(service?.durationMinutes) {
                            // previous function call addHours replaced with addMinutes xD
                            newEndDate = addMinutes(date, service?.durationMinutes)
                        } else {
                            // Default to 1 hour later
                            newEndDate = addHours(date, 1)
                        }

                        setReservation({
                            ...reservation,
                            expectedStartDate: date,
                            expectedEndDate: newEndDate
                        })
                    }}
                    label={t('Start Date & Time')}
                />

                <CDatePicker
                    value={reservation?.expectedEndDate}
                    marked={[
                        {
                            date: reservation?.expectedEndDate
                        }
                    ]}
                    onChange={(date) => {
                        setReservation({
                            ...reservation,
                            expectedEndDate: date
                        })
                    }}
                    label={t('End Date & Time')}
                />
            </Stack>

        )
    }

    const renderResources = () => {
        // Find the service associated with the current reservation
        const currentService = services.find(s => s.id === reservation?.serviceId)

        const linkedResources = resources.filter((resource) =>
            currentService?.resourceIds?.includes(resource.id as string)
        )

        const notLinkedResources = resources.filter(
            resource => !currentService?.resourceIds?.includes(resource.id as string)
        )

        const labeledResources = [
            ...linkedResources.map(resource => ({...resource, group: t('Linked resources')})),
            ...notLinkedResources.map(resource => ({...resource, group: t('Not linked resources')}))
        ]

        return (
            <Autocomplete
                id={'categories'}
                multiple

                // Sorting by availability & keeping the collection immutable
                options={[...(labeledResources || [])].sort((a, b) => {
                    const aAvailable = isResourceAvailable(a, resourcesWithAvailability, reservation)
                    const bAvailable = isResourceAvailable(b, resourcesWithAvailability, reservation)

                    if (aAvailable === bAvailable) return 0
                    return aAvailable ? -1 : 1
                })}

                groupBy={(option) =>
                    linkedResources.map(r => r.id).includes(option.id as string) ? t('Linked resources') : t('Not linked resources')
                }

                size={'small'}
                getOptionLabel={(option: Resource) => {
                    const categories = resourceCategories?.filter(c => option.categoryIds?.includes(c.id!))
                    const categoryNames = categories?.map(c => c.name).join(', ')
                    return [option.name || '', categoryNames].filter(Boolean).join(' - ')
                }}
                value={reservation?.resourceIds?.map(id => resources?.find(c => c.id === id)).filter(Boolean) as Resource[] || []}
                onOpen={() => {
                    // Force scroll to top when dropdown opens
                    setTimeout(() => {
                        const listbox = document.querySelector('#categories-listbox')
                        if (listbox) {
                            listbox.scrollTop = 0
                        }
                    }, 1)
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size={'small'}
                        label={t('Resources')}
                    />
                )}

                // Rendering options based on availability and resource attachment
                renderOption={(props, option: Resource) => {

                    const backgroundColor = isResourceAvailable(option, resourcesWithAvailability, reservation) ? CommonThemeColors.sys.success.light
                        : CommonThemeColors.sys.error.light

                    return (
                        <Box component="li" {...props} key={option.id} style={{display: 'flex', alignItems: 'center'}}>
                            <Chip
                                label={option.name}
                                style={{
                                    backgroundColor,
                                    color: 'black',
                                }}
                            />
                        </Box>
                    );
                }}

                renderGroup={params => (
                    <li key={params.key}>
                        <div style={{fontWeight: 'bold', padding: 10}}>{params.group}</div>
                        <ul style={{padding: 5}}>{params.children}</ul>
                    </li>
                )}
                fullWidth
                onChange={(e, value) => {
                    const newResources = [...value.map((v: any) => v.id)]
                    setReservation({
                        ...reservation,
                        resourceIds: newResources
                    })
                }}
                getOptionDisabled={(option) => reservation?.resourceIds?.includes(option.id as string) as boolean}
            />
        )
    }

    const renderNotes = () => {
        let renderIndex = 0
        let notes = reservation?.notes?.map((note, index) => {
            return (
                <PackageNoteField
                    key={renderIndex++}
                    value={note.note}
                    onCommit={(value) => {
                        const newNotes = [...(reservation.notes || [])]
                        newNotes[index] = {
                            ...newNotes[index],
                            note: value
                        }

                        // If note is empty, remove it
                        if (!value) {
                            newNotes.splice(index, 1)
                        }

                        setReservation({
                            ...reservation,
                            notes: newNotes
                        })
                    }}
                    label={note.noteType == "GUEST" ? t('Guest note') : t('Note')}
                    startAdornment={
                        <NoteGroupIcon
                            note={note}
                            index={noteGroupIndex}
                            sx={{mr: 1, alignSelf: 'flex-start', mt: '3px'}}
                        />
                    }
                />
            )
        })

        return (
            <Stack gap={1}>
                {notes}
            </Stack>
        )
    }

    return (
        <Stack gap={0.5}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
                <Typography variant={'h6'}>{service?.name || ''}</Typography>
                {reservation.reservationFromExtraPackageService
                    ? <Chip size={'small'} color={'warning'} variant={'outlined'} label={t('Extra service')}/>
                    : <Chip size={'small'} color={'primary'} variant={'outlined'} label={t('Included in package')}/>}
            </Stack>
            <Stack
                direction={'row'}
                gap={4}
                alignItems={'center'}
                justifyContent={'space-between'}
                width={'100%'}
            >
                <Stack gap={1} width="100%">
                    {renderDateAndTime()}
                    {renderResources()}
                    {renderNotes()}
                </Stack>

                <Stack direction={'row'} gap={1}>

                    {!readOnly && (
                        <IconButton
                            color={'primary'}
                            onClick={() => activateWizard()}
                        >
                            <AutoFixHighIcon/>
                        </IconButton>
                    )}

                    <IconButton
                        onClick={() => {
                            const newNotes = [...(reservation?.notes || [])]
                            newNotes.push({
                                note: '',
                                guestViewable: false
                            })
                            setReservation({
                                ...reservation,
                                notes: newNotes
                            })
                        }}
                    >
                        <Comment/>
                    </IconButton>

                    <IconButton
                        color={'error'}
                        onClick={() => setOpenConfirmDialog(true)}>
                        <DeleteIcon/>
                    </IconButton>

                </Stack>

            </Stack>

            <SimpleDialog
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                message={t('Remove reservation from package')}
                title={t("Are you sure?!")}
                confirmText={t('Yes')}
                cancelText={t('No')}
                onConfirm={async () => {
                    setOpenConfirmDialog(false)
                    onDelete()
                }}

            />
        </Stack>
    )
}

interface PackageNoteFieldProps {
    value?: string
    onCommit: (value: string) => void
    label: string
    startAdornment: React.ReactNode
}

// The in-progress package lives in the global reservations store, which the whole calendar reads.
// Committing on blur (not per keystroke) keeps typing a note from re-rendering the calendar each key.
const PackageNoteField = ({value, onCommit, label, startAdornment}: PackageNoteFieldProps) => {
    const [draft, setDraft] = useState(value || '')

    useEffect(() => {
        setDraft(value || '')
    }, [value])

    return (
        <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
                if (draft !== (value || '')) onCommit(draft)
            }}
            multiline
            size={'small'}
            label={label}
            InputProps={{startAdornment}}
        />
    )
}

export default AddNewPackageReservationsDialog;
